import React from 'react'
import { Check, Clock3, X } from 'lucide-react'
import Lang from '../../../config/lang'

const ApplicationProgressTimeline = ({
  items,
  lang,
  showBottomBorder = true,
}) => {
  const scrollContainerRef = React.useRef(null)
  const stepItemRefs = React.useRef([])

  React.useLayoutEffect(() => {
    let currentStepIndex = items.findIndex((step) => step.state === 'active')

    if (currentStepIndex === -1) {
      currentStepIndex = items.findIndex((step) => step.state === 'rejected')
    }

    if (currentStepIndex === -1) {
      const completedStepIndexes = items
        .map((step, index) => (step.state === 'done' ? index : -1))
        .filter((index) => index !== -1)

      currentStepIndex =
        completedStepIndexes.length > 0
          ? completedStepIndexes[completedStepIndexes.length - 1]
          : 0
    }

    const scrollContainer = scrollContainerRef.current
    const currentStepElement = stepItemRefs.current[currentStepIndex]

    if (!scrollContainer || !currentStepElement) return

    const containerWidth = scrollContainer.clientWidth
    const itemLeft = currentStepElement.offsetLeft
    const itemWidth = currentStepElement.offsetWidth

    scrollContainer.scrollLeft = Math.max(
      0,
      itemLeft - containerWidth / 2 + itemWidth / 2,
    )
  }, [items])

  const borderClassName = showBottomBorder ? 'border-y' : 'border-t'
  const paddingClassName = showBottomBorder ? 'py-6 sm:py-8' : 'pt-6 sm:pt-8'
  const scrollPaddingClassName = showBottomBorder ? 'pb-2' : ''

  return (
    <div className={`${borderClassName} border-[#E2E8F0] ${paddingClassName}`}>
      <p className='text-[14px] font-medium leading-[20px] text-[#111827]'>
        {Lang(lang, 'application_progress')}
      </p>

      <div
        ref={scrollContainerRef}
        className={`mt-6 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:mt-8 ${scrollPaddingClassName}`}
      >
        <div className='flex min-w-[620px] items-start sm:min-w-[776px]'>
          {items.map((step, index) => {
            const nextStep = items[index + 1]
            const connectorClassName =
              step.state === 'done' && nextStep?.state === 'done'
                ? 'bg-[#22C55E]'
                : 'bg-[#D1D5DB]'

            return (
              <React.Fragment key={step.label}>
                <div
                  ref={(element) => {
                    stepItemRefs.current[index] = element
                  }}
                  className='flex w-[64px] shrink-0 flex-col items-center sm:w-[74px]'
                >
                  <div
                    className={`flex h-[32px] w-[32px] items-center justify-center rounded-full ${step.bubbleClassName}`}
                  >
                    {step.state === 'done' ? (
                      <Check
                        className='h-[10px] w-[10px] text-white'
                        strokeWidth={3}
                      />
                    ) : step.state === 'rejected' ? (
                      <X
                        className='h-[10px] w-[10px] text-white'
                        strokeWidth={3}
                      />
                    ) : step.state === 'active' ? (
                      <Clock3
                        className='h-[10px] w-[10px] text-white'
                        strokeWidth={2.4}
                      />
                    ) : (
                      <span className='h-[8px] w-[8px] rounded-full bg-white' />
                    )}
                  </div>

                  <p
                    className={`mt-4 text-center text-[12px] font-medium leading-[20px] ${step.textClassName}`}
                  >
                    {step.label}
                  </p>
                </div>

                {index < items.length - 1 ? (
                  <div
                    className={`mt-[15px] h-[2px] w-[75px] shrink-0 sm:w-[108px] ${connectorClassName}`}
                  />
                ) : null}
              </React.Fragment>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default ApplicationProgressTimeline
